import maplibregl from 'maplibre-gl';

const PREFETCH_AHEAD = 3;
const STEP = 6;
const MIN_FH = 0;
const MAX_FH = 240;
const BEFORE_ID = 'countries-layer';
const BEFORE_ID_FALLBACK = 'labels-layer';

const padFh = (n) => String(n).padStart(3, '0');
const TRANSPARENT_VARS = new Set(['apcp', 'apcptotal']);

const WIND_ICON_EXPR = [
    'step', ['get', 'magnitude'],
    'wind-barb-00',
    2, 'wind-barb-01', 7, 'wind-barb-02', 12, 'wind-barb-03',
    17, 'wind-barb-04', 22, 'wind-barb-05', 27, 'wind-barb-06',
    32, 'wind-barb-07', 37, 'wind-barb-08', 42, 'wind-barb-09',
    47, 'wind-barb-10', 52, 'wind-barb-11', 57, 'wind-barb-12',
    62, 'wind-barb-13', 67, 'wind-barb-14', 72, 'wind-barb-15',
    77, 'wind-barb-16', 82, 'wind-barb-17', 87, 'wind-barb-18',
    92, 'wind-barb-19', 97, 'wind-barb-20', 102, 'wind-barb-21',
];

export class WeatherLayerManager {
    constructor(map, { prefetchAhead = PREFETCH_AHEAD } = {}) {
        this.map = map;
        this.prefetchAhead = prefetchAhead;
        this.fillLayers = new Map();
        this.windLayers = new Map();
        this.pendingRemovals = new Set();
        this.context = { init: null, model: null, variable: null };
        this.windContext = { init: null, model: null, presLev: null };
        this.activeFillKey = null;
        this.activeWindKey = null;
        this.popupRef = null;
        this.popupHandlerAttached = false;
        map.on('error', (e) => {
            if (e.sourceId && (e.sourceId.startsWith('fill::') || e.sourceId.startsWith('wind::'))) {
                e.preventDefault?.();
            }
        });
    }

    fillKey(init, model, variable, fhour) {
        return `fill::${init}::${model}::${variable}::${fhour}`;
    }

    windKey(init, model, presLev, fhour) {
        return `wind::${init}::${model}::${presLev}::${fhour}`;
    }

    wantedFhours(fhour) {
        const center = parseInt(fhour, 10);
        const out = new Set();
        for (let off = -this.prefetchAhead; off <= this.prefetchAhead; off++) {
            const h = center + off * STEP;
            if (h >= MIN_FH && h <= MAX_FH) out.add(padFh(h));
        }
        return out;
    }

    _resolveBeforeId(preferred) {
        if (preferred && this.map.getLayer(preferred)) return preferred;
        if (this.map.getLayer(BEFORE_ID)) return BEFORE_ID;
        if (this.map.getLayer(BEFORE_ID_FALLBACK)) return BEFORE_ID_FALLBACK;
        return undefined;
    }

    setActive(opts) {
        if (!this.map) return;
        if (this._deferTimer) {
            clearTimeout(this._deferTimer);
            this._deferTimer = null;
        }
        if (!this.map.getLayer(BEFORE_ID_FALLBACK)) {
            this._deferTimer = setTimeout(() => this.setActive(opts), 50);
            return;
        }
        const { init, model, variable, fhour, presLev, windEnabled, borderState, popupRef } = opts;
        console.log("SET ACTIVE:", {
          mapId: this.map.getContainer().id,
          init,
          model,
          variable,
          fhour,
        });
        this.popupRef = popupRef;

        const fillContextChanged =
            this.context.init !== init ||
            this.context.model !== model ||
            this.context.variable !== variable;
        if (fillContextChanged) {
            for (const key of Array.from(this.fillLayers.keys())) {
                this._removeFillImmediate(key);
            }
            this.context = { init, model, variable };
            this.activeFillKey = null;
        }

        const activeKey = this.fillKey(init, model, variable, fhour);
        const isTransparent = TRANSPARENT_VARS.has(variable);
        this._ensureFill(init, model, variable, fhour, borderState, BEFORE_ID, false);
        this._promote(activeKey);
        this.activeFillKey = activeKey;

        const wantedFh = this.wantedFhours(fhour);
        for (const key of Array.from(this.fillLayers.keys())) {
            const fh = key.split('::').pop();
            if (!wantedFh.has(fh)) this._scheduleFillRemoval(key);
        }
        for (const fh of wantedFh) {
            if (fh === fhour) continue;
            const k = this.fillKey(init, model, variable, fh);
            if (!this.fillLayers.has(k)) {
                this._ensureFill(init, model, variable, fh, borderState, activeKey, isTransparent);
            }
        }

        if (windEnabled) {
            const windContextChanged =
                this.windContext.init !== init ||
                this.windContext.model !== model ||
                this.windContext.presLev !== presLev;
            if (windContextChanged) {
                for (const key of Array.from(this.windLayers.keys())) {
                    this._removeWindImmediate(key);
                }
                this.windContext = { init, model, presLev };
                this.activeWindKey = null;
            }

            const activeWindKey = this.windKey(init, model, presLev, fhour);
            this._ensureWind(init, model, presLev, fhour, false);
            this._promoteWind(activeWindKey);
            this.activeWindKey = activeWindKey;

            for (const key of Array.from(this.windLayers.keys())) {
                const fh = key.split('::').pop();
                if (!wantedFh.has(fh)) this._scheduleWindRemoval(key);
            }
            for (const fh of wantedFh) {
                if (fh === fhour) continue;
                const k = this.windKey(init, model, presLev, fh);
                if (!this.windLayers.has(k)) {
                    this._ensureWind(init, model, presLev, fh, true);
                }
            }
        } else {
            for (const key of Array.from(this.windLayers.keys())) {
                this._scheduleWindRemoval(key);
            }
            this.windContext = { init: null, model: null, presLev: null };
            this.activeWindKey = null;
        }

        this._ensurePopupHandler();
    }

    _ensureFill(init, model, variable, fhour, borderState, beforeId, hidden) {
        const key = this.fillKey(init, model, variable, fhour);
        if (this.fillLayers.has(key)) {
            this.pendingRemovals.delete(this.fillLayers.get(key).layerId);
            return;
        }
        const sourceId = key;
        const layerId = key;
        const url = `pmtiles://${window.location.origin}/geojsons/${init}/${model}/${model}_${variable}_${fhour}.pmtiles`;
        try {
            if (this.map.getSource(sourceId)) {
                try { if (this.map.getLayer(layerId)) this.map.removeLayer(layerId); } catch {}
                try { this.map.removeSource(sourceId); } catch {}
            }
            this.map.addSource(sourceId, { type: 'vector', url });
            this.map.addLayer(
                {
                    id: layerId,
                    type: 'fill',
                    source: sourceId,
                    'source-layer': 'data',
                    paint: {
                        'fill-outline-color': borderState ? 'black' : 'transparent',
                        'fill-color': ['get', 'fill'],
                        'fill-opacity': hidden ? 0 : 1,
                    },
                },
                this._resolveBeforeId(beforeId)
            );
            this.fillLayers.set(key, { sourceId, layerId });
        } catch (err) {
            console.warn(`fill ensure failed: ${key}`, err);
        }
    }

    _ensureWind(init, model, presLev, fhour, hidden) {
        const key = this.windKey(init, model, presLev, fhour);
        if (this.windLayers.has(key)) {
            this.pendingRemovals.delete(this.windLayers.get(key).layerId);
            return;
        }
        const sourceId = key;
        const layerId = key;
        const url = `pmtiles://${window.location.origin}/geojsons/${init}/${model}/${model}_windbarbs${presLev}_${fhour}.pmtiles`;
        try {
            if (this.map.getSource(sourceId)) {
                try { if (this.map.getLayer(layerId)) this.map.removeLayer(layerId); } catch {}
                try { this.map.removeSource(sourceId); } catch {}
            }
            this.map.addSource(sourceId, { type: 'vector', url });
            this.map.addLayer({
                id: layerId,
                type: 'symbol',
                source: sourceId,
                'source-layer': 'data',
                layout: {
                    'icon-image': WIND_ICON_EXPR,
                    'icon-size': 0.25,
                    'icon-rotate': ['*', ['get', 'direction'], -1],
                    'icon-anchor': 'center',
                    'icon-rotation-alignment': 'viewport',
                    'symbol-placement': 'point',
                    'symbol-avoid-edges': true,
                    'icon-allow-overlap': true,
                    'visibility': hidden ? 'none' : 'visible',
                },
            });
            this.windLayers.set(key, { sourceId, layerId });
        } catch (err) {
            console.warn(`wind ensure failed: ${key}`, err);
        }
    }

    _promote(key) {
        if (TRANSPARENT_VARS.has(this.context.variable)) {
            for (const [k, info] of this.fillLayers) {
                try {
                    this.map.setPaintProperty(info.layerId, 'fill-opacity', k === key ? 1 : 0);
                } catch {}
            }
        }
        const info = this.fillLayers.get(key);
        if (!info) return;
        try {
            this.map.moveLayer(info.layerId, this._resolveBeforeId());
        } catch {}
    }

    _promoteWind(key) {
        for (const [k, info] of this.windLayers) {
            try {
                this.map.setLayoutProperty(info.layerId, 'visibility', k === key ? 'visible' : 'none');
            } catch {}
        }
    }

    _scheduleFillRemoval(key) {
        const info = this.fillLayers.get(key);
        if (!info) return;
        if (this.pendingRemovals.has(info.layerId)) return;
        this.pendingRemovals.add(info.layerId);
        const onIdle = () => {
            if (!this.pendingRemovals.has(info.layerId)) return;
            this.pendingRemovals.delete(info.layerId);
            this._removeFillImmediate(key);
        };
        this.map.once('idle', onIdle);
    }

    _scheduleWindRemoval(key) {
        const info = this.windLayers.get(key);
        if (!info) return;
        if (this.pendingRemovals.has(info.layerId)) return;
        this.pendingRemovals.add(info.layerId);
        const onIdle = () => {
            if (!this.pendingRemovals.has(info.layerId)) return;
            this.pendingRemovals.delete(info.layerId);
            this._removeWindImmediate(key);
        };
        this.map.once('idle', onIdle);
    }

    _removeFillImmediate(key) {
        const info = this.fillLayers.get(key);
        if (!info) return;
        this.fillLayers.delete(key);
        this.pendingRemovals.delete(info.layerId);
        try { if (this.map.getLayer(info.layerId)) this.map.removeLayer(info.layerId); } catch {}
        try { if (this.map.getSource(info.sourceId)) this.map.removeSource(info.sourceId); } catch {}
    }

    _removeWindImmediate(key) {
        const info = this.windLayers.get(key);
        if (!info) return;
        this.windLayers.delete(key);
        this.pendingRemovals.delete(info.layerId);
        try { if (this.map.getLayer(info.layerId)) this.map.removeLayer(info.layerId); } catch {}
        try { if (this.map.getSource(info.sourceId)) this.map.removeSource(info.sourceId); } catch {}
    }

    setBorderState(borderState) {
        const color = borderState ? 'black' : 'transparent';
        for (const { layerId } of this.fillLayers.values()) {
            try { this.map.setPaintProperty(layerId, 'fill-outline-color', color); } catch {}
        }
    }

    _ensurePopupHandler() {
        if (this.popupHandlerAttached) return;
        if (!this.popupRef) return;
        if (!this.popupRef.current) {
            this.popupRef.current = new maplibregl.Popup({
                closeButton: false,
                closeOnClick: false,
                offset: [0, -20],
            });
        }
        const popup = this.popupRef;
        this.map.on('mousemove', (e) => {
            const activeKey = this.activeFillKey;
            if (!activeKey) return;
            const info = this.fillLayers.get(activeKey);
            if (!info || !this.map.getLayer(info.layerId)) return;
            const features = this.map.queryRenderedFeatures(e.point, { layers: [info.layerId] });
            if (features.length > 0) {
                popup.current
                    .setLngLat(e.lngLat)
                    .setHTML(`<strong>Value:</strong> ${features[0].properties.title}`)
                    .addTo(this.map);
            } else {
                try { popup.current.remove(); } catch {}
            }
        });
        this.popupHandlerAttached = true;
    }

    cleanup() {
        for (const key of Array.from(this.fillLayers.keys())) this._removeFillImmediate(key);
        for (const key of Array.from(this.windLayers.keys())) this._removeWindImmediate(key);
        this.pendingRemovals.clear();
        this.context = { init: null, model: null, variable: null };
        this.windContext = { init: null, model: null, presLev: null };
        this.activeFillKey = null;
        this.activeWindKey = null;
    }
}
